/*
 * lp-angle-hero.js
 * Accessible autoplay control for the LP Angle Hero background video.
 *
 * - Provides a keyboard-operable pause/play <button> overlaid on the video
 *   (WCAG 2.2.2: pause, stop, hide for moving content).
 * - Gates autoplay behind prefers-reduced-motion: if the user prefers reduced
 *   motion the video is NOT auto-played. The poster / first frame is shown and
 *   the user can start playback with the button.
 * - In-app browsers (Instagram and Facebook on iOS) refuse or drop the first
 *   play() far more often than Safari: in September 2026 replays of the bleach
 *   page the hero was playing at load for 37% of Facebook and 50% of Instagram
 *   visitors against 80% in Safari. Meta preloads landing pages before showing
 *   them, and the webviews apply stricter media rules. So playback is retried
 *   when the page becomes visible, when the video scrolls into view, when its
 *   data arrives, and on the visitor's first tap anywhere; tapping the paused
 *   video itself starts it. A visitor's own pause is always respected.
 *
 * Implemented as a native Web Component. connectedCallback / disconnectedCallback
 * run automatically when the Shopify theme editor re-renders the section
 * (shopify:section:load / shopify:section:unload replace the section DOM),
 * so no manual section-event wiring is required.
 */
(function () {
  if (window.customElements && customElements.get('lp-hero-video')) return;
  if (!window.customElements) return;

  var REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  // Analytics only reach PostHog after analytics consent (anatomy-posthog.js before_send).
  function capture(name, props) {
    try {
      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture(name, props);
      }
    } catch (error) {}
  }

  function visibility() {
    return (typeof document !== 'undefined' && document.visibilityState) || 'visible';
  }

  customElements.define(
    'lp-hero-video',
    class LpHeroVideo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('[data-lp-hero-video]');
        this.toggle = this.querySelector('[data-lp-hero-video-toggle]');
        this.label = this.querySelector('[data-lp-hero-video-label]');

        if (!this.video || !this.toggle) return;

        this.labelPlay = this.getAttribute('data-label-play') || 'Play video';
        this.labelPause = this.getAttribute('data-label-pause') || 'Pause video';
        this.userPaused = false;
        this.blocked = false;
        this.autoplayReported = false;
        this.startReported = false;
        this.loadedAt = Date.now();

        this.onToggleClick = this.onToggleClick.bind(this);
        this.onVideoClick = this.onVideoClick.bind(this);
        this.onPlay = this.onPlay.bind(this);
        this.onPause = this.onPause.bind(this);
        this.onGesture = this.onGesture.bind(this);
        this.onVisibility = this.onVisibility.bind(this);
        this.onPageShow = this.onPageShow.bind(this);
        this.onLoadedData = this.onLoadedData.bind(this);

        this.toggle.addEventListener('click', this.onToggleClick);
        this.video.addEventListener('click', this.onVideoClick);
        this.video.addEventListener('play', this.onPlay);
        this.video.addEventListener('pause', this.onPause);

        this.ensureInlineMuted();

        // Drive autoplay from JS so prefers-reduced-motion can suppress it.
        // The <video> intentionally has no `autoplay` attribute in markup.
        if (this.prefersReducedMotion()) {
          this.syncUi();
          this.reportAutoplay('reduced_motion');
          return;
        }
        this.armRetries();
        this.tryPlay('autoplay');
      }

      disconnectedCallback() {
        if (this.toggle) this.toggle.removeEventListener('click', this.onToggleClick);
        if (this.video) {
          this.video.removeEventListener('click', this.onVideoClick);
          this.video.removeEventListener('play', this.onPlay);
          this.video.removeEventListener('pause', this.onPause);
        }
        this.disarmRetries();
      }

      prefersReducedMotion() {
        try {
          return window.matchMedia(REDUCED_MOTION_QUERY).matches;
        } catch (e) {
          return false;
        }
      }

      // Webviews read the muted / inline *properties*; set them explicitly as well as the attributes.
      ensureInlineMuted() {
        var video = this.video;
        try {
          video.muted = true;
          video.defaultMuted = true;
          video.playsInline = true;
        } catch (e) {}
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
      }

      armRetries() {
        var self = this;
        document.addEventListener('visibilitychange', this.onVisibility);
        window.addEventListener('pageshow', this.onPageShow);
        this.video.addEventListener('loadeddata', this.onLoadedData);
        this.armGesture();
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(function (entries) {
            for (var i = 0; i < entries.length; i += 1) {
              if (entries[i].isIntersecting) self.retry('in_view');
            }
          }, { threshold: 0.25 });
          this.observer.observe(this.video);
        }
        this.armed = true;
      }

      disarmRetries() {
        if (!this.armed) return;
        document.removeEventListener('visibilitychange', this.onVisibility);
        window.removeEventListener('pageshow', this.onPageShow);
        if (this.video) this.video.removeEventListener('loadeddata', this.onLoadedData);
        this.disarmGesture();
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
        this.armed = false;
      }

      // A tap anywhere is a user gesture, which in-app webviews accept for playback.
      armGesture() {
        if (this.gestureArmed) return;
        document.addEventListener('touchend', this.onGesture, { capture: true, passive: true });
        document.addEventListener('click', this.onGesture, { capture: true, passive: true });
        this.gestureArmed = true;
      }

      disarmGesture() {
        if (!this.gestureArmed) return;
        document.removeEventListener('touchend', this.onGesture, true);
        document.removeEventListener('click', this.onGesture, true);
        this.gestureArmed = false;
      }

      retry(trigger) {
        if (!this.video || this.userPaused || !this.video.paused) return;
        if (visibility() !== 'visible' || this.prefersReducedMotion()) return;
        this.tryPlay(trigger);
      }

      tryPlay(trigger) {
        var self = this;
        var playPromise;
        try {
          playPromise = this.video.play();
        } catch (error) {
          this.onBlocked(trigger, error);
          return;
        }
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise.then(
            function () {
              self.onStarted(trigger);
            },
            function (error) {
              self.onBlocked(trigger, error);
            }
          );
        } else if (!this.video.paused) {
          this.onStarted(trigger);
        }
        // Reflect whatever state we ended up in (playing, pending or blocked).
        this.syncUi();
      }

      onStarted(trigger) {
        if (this.blocked && !this.startReported) {
          this.startReported = true;
          capture('an_hero_video_started', {
            trigger: trigger,
            ms_since_load: Date.now() - this.loadedAt,
            page_visibility: visibility()
          });
        }
        this.blocked = false;
        this.reportAutoplay('playing', trigger);
        this.disarmGesture();
        this.syncUi();
      }

      onBlocked(trigger, error) {
        // AbortError means play() was interrupted by pause() or a new load, not refused.
        if (error && error.name === 'AbortError') return;
        this.blocked = true;
        this.reportAutoplay('blocked', trigger, error);
        this.syncUi();
      }

      reportAutoplay(outcome, trigger, error) {
        if (this.autoplayReported) return;
        this.autoplayReported = true;
        capture('an_hero_video_autoplay', {
          outcome: outcome,
          trigger: trigger || 'none',
          error_name: (error && error.name) || undefined,
          page_visibility: visibility()
        });
      }

      onToggleClick() {
        if (this.video.paused) {
          this.userPaused = false;
          this.tryPlay('toggle');
        } else {
          this.userPaused = true;
          this.video.pause();
        }
      }

      // Tapping the paused video starts it; a tap on a playing video leaves it playing (the button pauses).
      onVideoClick() {
        if (!this.video.paused) return;
        this.userPaused = false;
        this.tryPlay('tap');
      }

      onGesture(event) {
        var target = event && event.target;
        // The button and the video have their own handlers.
        if (target && (this.toggle.contains(target) || this.video.contains(target))) return;
        this.retry('gesture');
      }

      onVisibility() {
        if (visibility() === 'visible') this.retry('visible');
      }

      onPageShow() {
        this.retry('pageshow');
      }

      onLoadedData() {
        this.retry('loadeddata');
      }

      onPlay() {
        this.syncUi();
      }

      onPause() {
        this.syncUi();
      }

      syncUi() {
        var playing = !this.video.paused && !this.video.ended;
        var text = playing ? this.labelPause : this.labelPlay;
        this.toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
        this.toggle.setAttribute('aria-label', text);
        if (this.label) this.label.textContent = text;
        this.setAttribute('data-state', playing ? 'playing' : this.blocked ? 'blocked' : 'paused');
      }
    }
  );
})();
